import React, {useEffect, useMemo, useRef, useState} from 'react'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import type {OpiVolumeExpected} from './_requests'
import {NotifyButton} from './NotifyButton'
import {usePresence} from '../../../context/PresenceContext'

type Props = {
  items: OpiVolumeExpected[]
  lastUpdated: string
  nextRefreshAt: string
  /** Server remaining seconds — applied only when cache snapshot changes (initial load or post-background refresh). */
  secondsUntilRefresh: number
  cacheVersion: string
  onNotify: (languageCode: string, languageName: string) => void
  onExpired: () => void
  notifyingCode: string | null
}

export const VolumeExpectedPanel: React.FC<Props> = ({
  items,
  lastUpdated,
  nextRefreshAt,
  secondsUntilRefresh,
  cacheVersion,
  onNotify,
  onExpired,
  notifyingCode,
}) => {
  const {interpreters} = usePresence()
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const onExpiredRef = useRef(onExpired)
  const firedExpireRef = useRef(false)
  const appliedCacheVersionRef = useRef('')

  useEffect(() => {
    onExpiredRef.current = onExpired
  }, [onExpired])

  // Apply server remaining time ONLY when the cache snapshot changes (background refresh or first load).
  // Frontend never resets the timer on its own — it only ticks down locally.
  useEffect(() => {
    if (!cacheVersion || cacheVersion === appliedCacheVersionRef.current) return
    appliedCacheVersionRef.current = cacheVersion
    firedExpireRef.current = false
    setRemainingSeconds(Math.max(0, secondsUntilRefresh))
  }, [cacheVersion, secondsUntilRefresh])

  // Local 1s tick — no refetch, no reset.
  useEffect(() => {
    const id = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 0) return 0
        const next = prev - 1
        if (next <= 0 && !firedExpireRef.current) {
          firedExpireRef.current = true
          onExpiredRef.current()
        }
        return Math.max(0, next)
      })
    }, 1000)
    return () => window.clearInterval(id)
  }, [])

  const availableByLanguage = useMemo(() => {
    const map = new Map<string, number>()
    interpreters
      .filter((i) => i.status === 'Available')
      .forEach((i) => {
        i.languages?.forEach((l) => {
          extractLanguageNames(l.name).forEach((name) => {
            const key = name.toLowerCase()
            map.set(key, (map.get(key) ?? 0) + 1)
          })
        })
      })
    return map
  }, [interpreters])

  const countdownLabel = formatCountdown(remainingSeconds)
  const updatedLabel = formatApiWallClock(lastUpdated)
  const nextRefreshLabel = formatApiWallClock(nextRefreshAt)

  return (
    <aside className='opi-volume-panel'>
      <header className='opi-volume-panel__top'>
        <h3 className='opi-volume-panel__title'>Volume Expected</h3>
        <div className='opi-volume-panel__refresh' aria-live='polite'>
          <img src={toAbsoluteUrl('/media/icons/opi/refresh.svg')} alt='' />
          <span>
            Refresh in: {countdownLabel}
            {nextRefreshLabel !== '—' ? ` (${nextRefreshLabel})` : ''}
          </span>
        </div>
      </header>

      <p className='opi-volume-panel__subtitle'>Prediction based on last 3 months</p>
      <p className='opi-volume-panel__updated'>Last updated: {updatedLabel}</p>

      <div className='opi-volume-panel__list'>
        {items.length === 0 ? (
          <div className='opi-empty'>No volume predictions available</div>
        ) : (
          items.map((item) => {
            const avail =
              availableByLanguage.get(item.languageName.toLowerCase()) ??
              countAvailableForLanguage(interpreters, item.languageName)
            return (
              <article key={item.languageCode} className='opi-volume-panel__item'>
                <div className='opi-volume-panel__item-top'>
                  <div className='opi-volume-panel__lang-wrap'>
                    <span className='opi-volume-panel__rank'>#{item.rank}</span>
                    <span className='opi-volume-panel__lang' title={item.languageName}>
                      {item.languageName}
                    </span>
                  </div>
                  <NotifyButton
                    loading={notifyingCode === item.languageCode}
                    onClick={() => onNotify(item.languageCode, item.languageName)}
                  />
                </div>

                <div className='opi-volume-panel__stats'>
                  <div className='opi-volume-panel__stat'>
                    <span className='opi-volume-panel__stat-label'>Expected Calls</span>
                    <span className='opi-volume-panel__stat-box opi-volume-panel__stat-box--expected'>
                      {item.expectedCalls}
                    </span>
                  </div>
                  <div className='opi-volume-panel__stat'>
                    <span className='opi-volume-panel__stat-label'>Interpreters Available</span>
                    <span className='opi-volume-panel__stat-box opi-volume-panel__stat-box--available'>
                      {avail}
                    </span>
                  </div>
                </div>

                <p className='opi-volume-panel__item-footer'>
                  Last Notified{' '}
                  {item.lastNotified ? formatApiWallClock(item.lastNotified) : '—'}
                </p>
              </article>
            )
          })
        )}
      </div>
    </aside>
  )
}

function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const m = Math.floor(safe / 60)
  const s = safe % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/** Display wall-clock already converted on API (UTC → user profile TZ). */
function formatApiWallClock(value: string): string {
  if (!value) return '—'
  const match = value.match(/T(\d{2}):(\d{2})/)
  if (!match) return '—'
  const hours = parseInt(match[1], 10)
  const minutes = match[2]
  const h12 = hours % 12 || 12
  const ampm = hours >= 12 ? 'PM' : 'AM'
  return `${h12}:${minutes} ${ampm}`
}

function extractLanguageNames(pairOrName: string): string[] {
  if (!pairOrName) return []
  const parts = pairOrName.split(/\s[-–/]\s/).map((p) => p.trim()).filter(Boolean)
  return parts.length > 0 ? parts : [pairOrName.trim()]
}

function countAvailableForLanguage(
  interpreters: ReturnType<typeof usePresence>['interpreters'],
  languageName: string
): number {
  const needle = languageName.toLowerCase()
  return interpreters.filter(
    (i) =>
      i.status === 'Available' &&
      i.languages?.some((l) =>
        extractLanguageNames(l.name).some((n) => n.toLowerCase() === needle)
      )
  ).length
}
